import React, { useEffect, useState } from "react";
import Layout from "../../Layout";
import { motion } from "framer-motion";
import {
  connectConnectorApi,
  createConnectorsApi,
  editConnectorApi,
  getConnectedConnectorsApi,
  getEndUsersByCompanyApi,
} from "../../../services/axios/AuthApis/AssetManagementApis";
import { useAuth } from "../../../context/AuthContext";
import { useSnackbar } from "../../../context/SnackbarContext";
import { Await } from "react-router-dom";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

const IntegrationModal = ({
  open,
  onClose,
  selectedConnector,
  customerConnectors,
  setCustomerConnectors,
  integrationMode,
  integrationData,
}) => {
  const { user } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    accessKey: "",
    secretKey: "",
    tertiary: "",
    criticality: "",
    owner: "",
  });
  const [owners, setOwners] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const isCustomerAdmin = user.role === "customer_admin";
  let customerId;
  let customerMasterId;
  if (isCustomerAdmin) {
    customerId = user?.id;
    customerMasterId = user?.customer_master_id;
  }
  console.log({ selectedConnector: selectedConnector });
  const connector_name = selectedConnector?.name.toLowerCase();
  const isApiKey = selectedConnector?.connector_type === "APIKEY";
  const isOAuth = selectedConnector?.connector_type === "OAUTH2";
  const isSCIM = selectedConnector?.connector_type === "SCIM";
  const isBasicAuth = selectedConnector?.connector_type === "BASICAUTH";
  const isPAT = selectedConnector?.connector_type === "PAT";
  const isDocusign = connector_name === "docusign";
  const isJira = connector_name === "jira";
  const isAzure = connector_name === "microsoft azure";
  const isOdoo = connector_name === "odoo";
  const isEdit = integrationMode === "edit" && !!integrationData;

  useEffect(() => {
    if (integrationMode === "edit" && integrationData) {
      // The stored secret is never sent to the browser. Left blank, the
      // server keeps the one it has.
      setFormData({
        accessKey: integrationData.access_key || "",
        secretKey: "",
        tertiary: integrationData.tertiary_credential || "",
        criticality: integrationData.criticality || "",
        owner: integrationData.owner || "",
      });
    }

    if (integrationMode === "create") {
      setFormData({
        accessKey: "",
        secretKey: "",
        tertiary: "",
        criticality: "",
        owner: "",
      });
    }
  }, [integrationMode, integrationData]);

  useEffect(() => {
    getOwners(customerMasterId);
  }, [isCustomerAdmin]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        customer_id: customerId,
        connector_id: selectedConnector.id,
      };

      const response = await connectConnectorApi(payload);
      // if (response?.consent_required && response?.consent_url) {
      //   toast.info("Redirecting to DocuSign for consent...");

      //   window.location.href = response.consent_url;
      //   return;
      // }

      // GCP uses a real OAuth 2.0 Authorization Code flow - the backend
      // can't complete it synchronously inside this POST, so instead it
      // returns oauth_required/oauth_url (mirroring the DocuSign
      // consent_required/consent_url shape above) and we redirect the
      // browser straight to Google's consent screen. The connection is
      // NOT complete yet at this point, so don't show a success toast or
      // close the modal - the browser is about to navigate away entirely,
      // and the asset management page picks the result back up via a
      // ?gcp_oauth= query param once Google redirects back.
      if (response?.oauth_required && response?.oauth_url) {
        showSnackbar("Redirecting to Google for consent...", "info");
        window.location.href = response.oauth_url;
        return;
      }

      showSnackbar("Connector connected successfully", "success");

      setCustomerConnectors((prev) => {
        if (integrationMode === "edit" && integrationData) {
          return prev.map((connector) =>
            connector.id === integrationData.id
              ? {
                  ...connector,
                  access_key: formData.accessKey,
                  tertiary_credential: formData.tertiary,
                  criticality: formData.criticality,
                  owner: formData.owner,
                  connected: true,
                  updated_at: new Date().toISOString(),
                }
              : connector,
          );
        } else {
          return [
            ...prev,
            {
              id: selectedConnector.id,
              connector_id: selectedConnector.id,
              connector_name: selectedConnector.name,
              customer_id: customerId,
              connected: true,
              access_key: formData.accessKey,
              tertiary_credential: formData.tertiary,
              criticality: formData.criticality,
              owner: formData.owner,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
          ];
        }
      });

      onClose();
    } catch (error) {
      console.error("Error creating connection:", error);
      showSnackbar("Failed to connect", "error");
    } finally {
      setLoading(false);
    }
  };

  const getOwners = async (customerMasterId) => {
    try {
      const response = await getEndUsersByCompanyApi(customerMasterId);
      console.log({ response: response });
      setOwners(response);
    } catch (err) {
      console.error("Failed fetch Owners", err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
      >
        <h2 className="text-xl font-semibold text-gray-800 mb-5">
          {selectedConnector?.name || "Create Connection"}
        </h2>

        <form onSubmit={handleSubmit} autoComplete="off" className="space-y-5">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              {isApiKey && isOdoo
                ? "Username"
                : isApiKey
                  ? "Access Key"
                  : isOAuth && isDocusign
                    ? "Integration Key"
                    : isOAuth
                      ? "Client ID"
                      : isSCIM
                        ? "Base URL"
                        : isBasicAuth && isJira
                          ? "Email ID"
                          : isBasicAuth
                            ? "Username"
                            : isPAT
                              ? "Base URL"
                              : ""}
            </label>
            <input
              type="text"
              name="accessKey"
              value={formData.accessKey}
              onChange={handleChange}
              required
              placeholder={
                isApiKey && isOdoo
                  ? "Enter Username"
                  : isApiKey
                    ? "Enter Access Key"
                    : isOAuth && isDocusign
                      ? "Enter Integration Key"
                      : isOAuth
                        ? "Enter Client ID"
                        : isSCIM
                          ? "Enter Base URL"
                          : isBasicAuth && isJira
                            ? "Enter Email ID"
                            : isBasicAuth
                              ? "Enter Username"
                              : isPAT
                                ? "Enter Base URL"
                                : ""
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              {isApiKey && isOdoo
                ? "API Key"
                : isApiKey
                  ? "Secret Key"
                  : isOAuth && isDocusign
                    ? "DocuSign User ID"
                    : isOAuth
                      ? "Secret Key"
                      : isSCIM
                        ? "SCIM Token"
                        : isBasicAuth && isJira
                          ? "API Token"
                          : isBasicAuth
                            ? "Password"
                            : isPAT
                              ? "Personal Access Token(PAT)"
                              : ""}
            </label>

            <input
              autoComplete="new-password"
              type={showPassword ? "text" : "password"}
              name="secretKey"
              value={formData.secretKey}
              onChange={handleChange}
              required={!isEdit}
              placeholder={
                isEdit
                  ? "Leave blank to keep the current value"
                  : isApiKey && isOdoo
                  ? "Enter API Key"
                  : isApiKey
                    ? "Enter Secret Key"
                    : isOAuth && isDocusign
                      ? "Enter DocuSign User ID"
                      : isOAuth
                        ? "Enter Secret Key"
                        : isSCIM
                          ? "Enter SCIM Token"
                          : isBasicAuth && isJira
                            ? "Enter API Token"
                            : isBasicAuth
                              ? "Enter Password"
                              : isPAT
                                ? "Enter Personal Access Token"
                                : ""
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 text-sm mb-3"
            />
            <span
              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </span>

            {isJira || isAzure || isOdoo ? (
              <div className="flex flex-col gap-1">
                {" "}
                <label className="text-sm font-medium text-gray-700">
                  {isAzure
                    ? "Tenant ID"
                    : isJira
                      ? "Base URL"
                      : isOdoo
                        ? "Base URL"
                        : ""}
                </label>
                <input
                  type="text"
                  name="tertiary"
                  value={formData.tertiary}
                  onChange={handleChange}
                  placeholder={
                    isAzure
                      ? "Enter Tenant ID"
                      : isJira
                        ? "Enter Base URL"
                        : isOdoo
                          ? "Enter Base URL"
                          : ""
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 text-sm mb-3"
                />
                {/* <span
                    className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
                    onClick={() => setShowPassword(!showPassword)}
                  ></span> */}
              </div>
            ) : (
              ""
            )}

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Type</label>
              <select
                value={formData.criticality}
                name="criticality"
                required
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                <option value="">Select Criticality</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Owner</label>
            <select
              value={formData.owner}
              name="owner"
              required
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              <option value="">Select Owner</option>
              {owners.map((owner) => (
                <option key={owner.user.id} value={owner.user.id}>
                  {owner.user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-60"
            >
              {loading ? "Connecting..." : "Connect"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default IntegrationModal;
